'use client';

import {
  ContentService,
  type Content,
  ContentOptions
} from '@/services/isomorphic/ContentService';
import type { Nullable } from '@/type-utils';
import { useEffect, useState } from 'react';
import { hashKeys } from '@/utils/hash-utils';
import { usePreview } from '../components/dev/Preview/usePreview';

/**
 * Gets content on the client side. Use this if a piece of content cannot be fetched in a
 * server component, e.g. it lazy loaded or it depends on client-side state.
 * @param contentOptions - The options for the content service.
 * @returns A content object.
 */ export function useContent(
  contentOptions: ContentOptions
): Nullable<Content> {
  const [preview] = usePreview(); // Bust the cache when the current preview changes.
  const [content, setContent] = useState<Nullable<Content>>(null);

  const [prevOptions, setPrevOptions] = useState(contentOptions);
  // Reset the content if the options change.
  if (hashKeys([prevOptions]) !== hashKeys([contentOptions])) {
    setPrevOptions(contentOptions);
  }

  // Fetch the content for the modal.
  useEffect(
    function getContent() {
      const fetchContent = async (): Promise<void> => {
        const content = await ContentService.getContent(prevOptions);
        setContent(content);
      };

      // eslint-disable-next-line no-void -- Effects cannot be async.
      void fetchContent();
    },
    [prevOptions, preview]
  );

  return content;
}
